import { useState, useCallback, useEffect } from 'react'
import type { ConversationSummary } from './api'
import * as api from './api'

export interface UseConversationListResult {
  conversations: ConversationSummary[]
  activeConversationId: string | null
  loading: boolean
  refetch: () => Promise<void>
  updateConversationPreview: (id: string, lastMessage: string, updatedAt: string) => void
  updateConversationTitle: (id: string, title: string) => void
}

export function useConversationList(): UseConversationListResult {
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [loading, setLoading] = useState(true)

  const fetchConversations = useCallback(async () => {
    try {
      const { conversations: convs } = await api.getConversations()
      setConversations(convs)
    } catch (err) {
      console.error('Failed to load conversations:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchConversations()
  }, [fetchConversations])

  const refetch = useCallback(async () => {
    await fetchConversations()
  }, [fetchConversations])

  const updateConversationPreview = useCallback((id: string, lastMessage: string, updatedAt: string) => {
    setConversations(prev => {
      const idx = prev.findIndex(c => c.id === id)
      if (idx === -1) return prev
      const updated = [...prev]
      updated[idx] = { ...updated[idx]!, lastMessage, updatedAt }
      // Re-sort: move updated conversation to top
      const [item] = updated.splice(idx, 1)
      updated.unshift(item!)
      return updated
    })
  }, [])

  const updateConversationTitle = useCallback((id: string, title: string) => {
    setConversations(prev => prev.map(c => c.id === id ? { ...c, title } : c))
  }, [])

  const activeConversationId = conversations.find(c => c.isActive)?.id ?? null

  return { conversations, activeConversationId, loading, refetch, updateConversationPreview, updateConversationTitle }
}
